import React, { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../components/user/BCPAuthProvider";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import * as Yup from "yup";
import { Formik } from "formik";

import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Hidden,
  IconButton,
  Link,
  MenuItem,
  Modal,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";

import {
  BCPCopy,
  BCPDiscord,
  BCPFacebook,
  BCPInstagram,
  BCPTwitterX,
  BCPWrench,
  BCPXmark,
} from "../../components/icons/bcp/BCPIcons";

import ListResponsive from "../../components/other/ListResponsive";
import {
  delay,
  deleteData,
  findGetParameter,
  getData,
  newPath,
  postData,
  titleCase,
} from "../../components/utilities/UtilityFunctions";
import { postNotification } from "../../components/utilities/Notifications";

import ModalLoadingFull from "../../components/modals/ModalLoadingFull";
import {
  getArmyPickerArmies,
  handlePagingAll,
} from "../../components/api/APIUtilities";
import CustomChip from "../../components/utilities/CustomChip";

import { useConfirm } from "material-ui-confirm";
import StyledMenu from "../../components/layout/StyledMenu";
import ArmyPicker from "../../components/utilities/ArmyPicker";
import GenericPicker from "../../components/utilities/GenericPicker";
import ImageLoader from "../../components/utilities/ImageLoader";
import TabPanel, { tabPanelA11yProps } from "../../components/layout/TabPanel";
import { getEnvironmentVariable } from "../../global/environmentVariables";

const fetchUser = async (userId) => {
  if (!userId) return;

  var returnObj;
  try {
    returnObj = await getData(`/users/${userId}`);
  } catch (err) {
    console.error(err);
  }
  return returnObj;
};

const fetchLeague = async (leagueId, authContext) => {
  if (!leagueId) return;

  var returnObj;
  try {
    returnObj = await getData(`/leagues/${leagueId}`, {
      queryStringParameters: {
        "expand[]": ["gameSystem", "gameStore", "gameStoreUser"],
      },
    });

    if (
      returnObj?.hidePlacings &&
      authContext?.authState?.identity?.isAdmin !== true
    ) {
      if (
        !returnObj.gameStoreUser?.active ||
        (!returnObj.gameStoreUser?.owner &&
          !returnObj.gameStoreUser?.permissions?.createLeagues)
      ) {
        let error = new Error(
          "The Circuit owner currently has Rankings hidden."
        );
        error.name = "HidePlacings Error";
        throw error;
      }
    }

    if (returnObj?.photoFileId) {
      returnObj.bannerFile = await fetchFile(returnObj.photoFileId);
    }
  } catch (err) {
    console.error(err);
  }
  return returnObj;
};

const fetchFile = async (fileId) => {
  if (!fileId) return;

  var returnObj;
  try {
    returnObj = await getData(`/files/${fileId}`, {
      queryStringParameters: {},
    });
  } catch (err) {
    console.error(err);
  }
  return returnObj;
};

async function parallel1(options) {
  const taskUser = options.userId ? fetchUser(options.userId) : null;
  const taskLeague = options.leagueId
    ? fetchLeague(options.leagueId, options.authContext)
    : null;

  return {
    user: await taskUser,
    league: await taskLeague,
  };
}

const initialAchievements = {
  Bronze: {
    leagueId: "62942a3aeead165c9d69afb3",
    level: "Bronze",
    chip: {
      background: "#8A4E16",
      border: "#FFFFFF",
      color: "#FFFFFF",
    },
    current: { totalPoints: 0 },
    lifeTime: { totalPoints: 0 },
  },
  Silver: {
    leagueId: "62942a3aeead165c9d69afb3",
    level: "Silver",
    chip: {
      background: "#D7DFDE",
      border: "#FFFFFF",
      color: "#5F736F",
    },
    current: { totalPoints: 0 },
    lifeTime: { totalPoints: 0 },
  },
  Gold: {
    leagueId: "62942a3aeead165c9d69afb3",
    level: "Gold",
    chip: {
      background: "#CE9911",
      border: "#FFFFFF",
      color: "#FFF6DE",
    },
    current: { totalPoints: 0 },
    lifeTime: { totalPoints: 0 },
  },
  Platinum: {
    leagueId: "62942a3aeead165c9d69afb3",
    level: "Platinum",
    chip: {
      background: "#DEF4F8",
      border: "#FFFFFF",
      color: "#43828D",
    },
    current: { totalPoints: 0 },
    lifeTime: { totalPoints: 0 },
  },
};

const tabIndexes = {
  overview: 0,
  about: 1,
  // circuits: 2,
};

export default function Profile(props) {
  const { appRoute } = props;
  const { t } = useTranslation("translation");
  let { userId } = useParams();
  const activeTabParam = findGetParameter("active_tab");
  const navigate = useNavigate();

  const authContext = useContext(AuthContext);
  const confirm = useConfirm();
  const [referralTooltip, setReferralTooltip] = useState(false);

  const embed = appRoute || findGetParameter("embed") === "true";
  const leagueId = findGetParameter("league");

  const [loading, setLoading] = useState(true);
  const [player, setPlayer] = useState(null);

  const [activeTab, setActiveTab] = useState(null);
  const handleTabChange = async (e, newValue) => {
    setLoading(true);

    var path;
    Object.keys(tabIndexes).forEach((tabIndex) => {
      if (tabIndexes[tabIndex] === newValue) {
        var params = {
          active_tab: tabIndex,
        };
        path = newPath(params);

        return true;
      }
    });
    setActiveTab(newValue);
    navigate(path, { replace: false });
    setLoading(false);
  };

  const [placing, setPlacing] = useState(null);
  const [league, setLeague] = useState(null);
  const [hidePlacings, setHidePlacings] = useState(false);
  const [results, setResults] = useState(null);

  const [achievements, setAchievements] = useState(null);

  const [manageCorrections, setManageCorrections] = useState(false);
  const [optionsAnchorEl, setOptionsAnchorEl] = useState(null);
  const [correctionType, setCorrectionType] = useState(null);
  const [correction, setCorrection] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [refresh, setRefresh] = useState(false);

  const go = async () => {
    try {
      setLoading(true);

      setPlacing(null);
      setResults(null);

      var initialTab =
        tabIndexes[
          activeTabParam ? activeTabParam.toLowerCase() : "overview"
        ] !== undefined
          ? tabIndexes[
              activeTabParam ? activeTabParam.toLowerCase() : "overview"
            ]
          : 0;
      setActiveTab(initialTab);

      var parallelData1 = await parallel1({
        userId: userId,
        leagueId: leagueId,
        authContext: authContext,
      });
      var apiUser = parallelData1.user;
      var apiLeague = parallelData1.league;

      if (
        authContext?.authState?.identity?.isAdmin === true ||
        apiLeague?.gameStoreUser?.owner ||
        (apiLeague?.gameStoreUser?.active &&
          apiLeague?.gameStoreUser?.corrections)
      ) {
        setManageCorrections(true);
      }

      setPlayer(apiUser);

      setLoading(false);

      try {
        if (leagueId) {
          let queryStringParameters = {
            placingsType:
              apiLeague?.defaultPlacingType === "ELO Points" ? "elo" : "player",
          };

          let apiFetch = await getData(
            `/placings/user/${userId}/league/${leagueId}`,
            {
              queryStringParameters: queryStringParameters,
            }
          );
          var apiPlacing = {
            ...apiFetch?.data?.[0],
            winRatio: 0,
          };

          if (apiPlacing.resultRecord?.length) {
            apiPlacing.winRatio =
              Math.round(
                (apiPlacing.resultRecord.reduce(
                  (partialSum, a) => partialSum + a,
                  0
                ) /
                  2 /
                  apiPlacing.resultRecord.length) *
                  10000
              ) / 100;
          }

          setPlacing(apiPlacing);
        }
      } catch (err) {
        //
      }

      try {
        let queryStringParameters = {
          limit: 100,
          userId: userId,
          "expand[]": ["event", "team", "army", "subFaction", "character"],
        };
        var apiResults;
        if (leagueId) {
          queryStringParameters.leagueId = leagueId;

          if (apiLeague?.hidePlacings) {
            let error = new Error(
              "The Circuit owner currently has Rankings hidden."
            );
            error.name = "HidePlacings Error";
            if (!authContext?.authState?.identity?.userId) {
              throw error;
            }

            var leagueRoles = await getData(`/leagues/${leagueId}/roles`);
            if (!leagueRoles?.owner && !leagueRoles?.createLeagues) {
              throw error;
            }
          }

          setLeague(apiLeague);
        }

        apiResults = await handlePagingAll(
          `/eventplacings`,
          queryStringParameters
        );
        apiResults.sort((a, b) => {
          if (apiLeague?.defaultPlacingType !== "ELO Points") {
            if (a.points !== b.points) {
              if (a.points || b.points) {
                if (!a.points) return 1;
                if (!b.points) return -1;
                if (a.points !== b.points) return b.points - a.points;
              }
            }
          }

          if (a.placing || b.placing) {
            if (!a.placing) return 1;
            if (!b.placing) return -1;
            return a.placing - b.placing;
          }

          return 0;
        });

        var filteredResults = [];
        var filteredResultsDict = {};
        for (let apiResult of apiResults) {
          if (!filteredResultsDict[apiResult.event?.id]) {
            filteredResultsDict[apiResult.event?.id] = true;
            filteredResults.push(apiResult);
          }
        }

        setResults(filteredResults);
      } catch (err) {
        if (err.name === "HidePlacings Error") {
          postNotification("warning", err.message);
          setHidePlacings(true);
          setResults([]);
        } else {
          postNotification("error", err);
        }
      }

      if (getEnvironmentVariable("IS_SOLFORGE") === "true") {
        let apiPlayerPlacings = await handlePagingAll(`/placings`, {
          placingsType: "player",
          userId: userId,
          limit: 500,
          "expand[]": ["league"],
        });

        var newAchievements = {
          ...initialAchievements,
        };

        for (let apiPlayerPlacing of apiPlayerPlacings) {
          if (apiPlayerPlacing?.league?.bcpDefault) {
            var level = apiPlayerPlacing.league.name?.split(" ")?.[0];
            if (level && newAchievements[level]) {
              newAchievements[level][
                apiPlayerPlacing?.league?.lifeTime ? "lifeTime" : "current"
              ] = apiPlayerPlacing;
            }
          }
        }

        setAchievements(newAchievements);
      }
    } catch (err) {
      postNotification("error", err);
    }
  };

  useEffect(() => {
    go();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const delayOff = async () => {
      await delay(3 * 1000);
      setReferralTooltip(false);
    };
    if (referralTooltip) {
      delayOff();
    }
  }, [referralTooltip]);

  useEffect(() => {
    const doCorrection = async () => {
      if (correctionType === "deletePlacing") {
        if (!correction.id) {
          // not ready
          return;
        }
        try {
          await confirm({
            title: `Are you sure?`,
            description: `Are you sure you want to delete this players results for ${correction.event?.name?.trim()}? This will only affect the current circuit`,
            dialogProps: {
              sx: {
                zIndex: 99999,
              },
            },
          });
        } catch (err) {
          return;
        }
        setProcessing(true);
        try {
          await deleteData(`/leagues/${league.id}/deletePlacing`, {
            body: {
              placingId: correction.id,
            },
          });
          await delay(3000);
          setCorrectionType(null);
          setCorrection(null);
          setRefresh(true);
        } catch (err) {
          postNotification("error", err);
        }
        setProcessing(false);
      } else if (correctionType === "zeroPlacing") {
        if (!correction.id) {
          // not ready
          return;
        }
        try {
          await confirm({
            title: `Are you sure?`,
            description: `Are you sure you want to zero this players results for ${correction.event?.name?.trim()}? This will only affect the current circuit`,
            dialogProps: {
              sx: {
                zIndex: 99999,
              },
            },
          });
        } catch (err) {
          setCorrectionType(null);
          setCorrection(null);
          return;
        }
        setProcessing(true);
        try {
          await postData(`/leagues/${league.id}/zeroPlacing`, {
            body: {
              placingId: correction.id,
            },
          });
          await delay(3000);
          setCorrectionType(null);
          setCorrection(null);
          setRefresh(true);
        } catch (err) {
          setCorrectionType(null);
          setCorrection(null);
          postNotification("error", err);
        }
        setProcessing(false);
      }
    };

    if (correctionType && correction) {
      doCorrection();
    }
    // eslint-disable-next-line
  }, [correctionType]);

  useEffect(() => {
    if (refresh) {
      setRefresh(false);
      go();
    }
    // eslint-disable-next-line
  }, [refresh]);

  return (
    <>
      {(loading || processing) && <ModalLoadingFull />}
      {!loading && player && (
        <Box sx={{ width: "100%" }}>
          <Box sx={{ width: "100%", backgroundColor: "background.secondary" }}>
            <Container maxWidth="lg">
              <Grid container sx={{ gap: 2 }}>
                <ImageLoader
                  alt={`${player?.firstName} ${player?.lastName}`}
                  imageFileId={player?.bannerFileId}
                  background
                  wrapperSx={{
                    borderBottomLeftRadius: "8px",
                    borderBottomRightRadius: "8px",
                    height: ["250px", "350px"],
                  }}
                />
                <Grid item xs={12} sx={{ position: "relative" }}>
                  <Box
                    sx={{
                      position: "absolute",
                      top: "-90px",
                      left: "50%",
                      transform: "translate(-50%, 0%)",
                    }}
                  >
                    <ImageLoader
                      alt={`${player?.firstName} ${player?.lastName}`}
                      imageFileId={player?.profileFileId}
                      avatar
                      wrapperSx={{
                        width: 150,
                        height: 150,
                        backgroundColor: "#fff",
                        border: "3px solid #fff",
                      }}
                    />
                  </Box>

                  {Boolean(
                    player.socials?.Facebook ||
                      player.socials?.Twitter ||
                      player.socials?.Instagram ||
                      player.socials?.Discord
                  ) && (
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: "8px",
                        right: "8px",
                      }}
                    >
                      {player.socials?.Facebook && (
                        <IconButton
                          color="primary"
                          variant="contained"
                          sx={{ ml: "4px" }}
                          href={`https://facebook.com/${player.socials.Facebook}`}
                          target="_blank"
                        >
                          <BCPFacebook />
                        </IconButton>
                      )}
                      {player.socials?.Twitter && (
                        <IconButton
                          color="primary"
                          variant="contained"
                          sx={{ ml: "4px" }}
                          href={`https://twitter.com/${player.socials.Twitter}`}
                          target="_blank"
                        >
                          <BCPTwitterX />
                        </IconButton>
                      )}
                      {player.socials?.Instagram && (
                        <IconButton
                          color="primary"
                          variant="contained"
                          sx={{ ml: "4px" }}
                          href={`https://instagram.com/${player.socials.Instagram}`}
                          target="_blank"
                        >
                          <BCPInstagram />
                        </IconButton>
                      )}
                      {player.socials?.Discord && (
                        <IconButton
                          color="primary"
                          variant="contained"
                          sx={{ ml: "4px" }}
                          onClick={() => {
                            navigator.clipboard.writeText(
                              player.socials?.Discord
                            );
                            postNotification(
                              "info",
                              "Discord ID copied to clipboard"
                            );
                          }}
                        >
                          <BCPDiscord
                            style={{
                              verticalAlign: "bottom",
                            }}
                          />
                        </IconButton>
                      )}
                    </Box>
                  )}
                  <Box
                    sx={{
                      p: 2,
                      pt: "70px",
                      textAlign: "center",
                      ...(Boolean(
                        player.socials?.Facebook ||
                          player.socials?.Twitter ||
                          player.socials?.Instagram ||
                          player.socials?.Discord
                      ) && {
                        pb: ["50px", "50px", 0],
                      }),
                    }}
                  >
                    <Typography variant="h3" gutterBottom>
                      {`${player?.firstName || player?.user?.firstName}${
                        player.nickname ? ` "${player.nickname}" ` : " "
                      }${player?.lastName || player?.user?.lastName}`}
                    </Typography>
                    {player.accountBadges?.length > 0 && (
                      <Grid container spacing={1} justifyContent="center">
                        {player.accountBadges.map((accountBadge, index) => (
                          <Grid item key={index}>
                            <CustomChip
                              label={accountBadge.label}
                              wrapperSX={{
                                background: accountBadge.background,
                                color: accountBadge.color,
                                border: `2px solid ${accountBadge.border}`,
                              }}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    )}
                  </Box>
                </Grid>
              </Grid>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                aria-label="profile tabs"
                sx={{ backgroundColor: "inherit" }}
              >
                {Object.keys(tabIndexes).map((key) => (
                  <Tab
                    key={key}
                    label={titleCase(key)}
                    {...tabPanelA11yProps(tabIndexes[key], "profile")}
                  />
                ))}
              </Tabs>
            </Container>
          </Box>
          <Container sx={{ pt: 2 }} maxWidth="lg">
            <Box
              sx={{
                p: 2,
                borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                borderRadius: 1,
                boxShadow: 1,
                backgroundColor: "background.secondary",
              }}
            >
              <TabPanel value={activeTab} index={tabIndexes.overview}>
                <Typography variant="h3" gutterBottom>
                  {league?.name ? `${league?.name} ` : ""}Overview
                </Typography>
                <Divider sx={{ mb: 1 }} />
                <Grid container sx={{ gap: 2 }}>
                  {getEnvironmentVariable("SHOW_AMBASSADOR") === "true" &&
                    getEnvironmentVariable("IS_SOLFORGE") === "true" &&
                    userId === authContext?.authState?.identity?.userId && (
                      <Grid
                        item
                        xs={12}
                        sx={{
                          p: 2,
                          borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                          borderRadius: 1,
                          boxShadow: 1,
                          backgroundColor: "background.secondary",
                          textAlign: "left",
                        }}
                      >
                        <Typography variant="h4" gutterBottom>
                          Guardian Points:{" "}
                          {authContext?.authState?.identity?.ambassadorPoints ||
                            0}
                        </Typography>
                        <Box
                          sx={{
                            mb: 2,
                            p: "6px 16px",
                            borderRadius: "4px",
                            backgroundColor: "#A55645",
                            color: "#ffffff",
                          }}
                        >
                          <Grid container sx={{ pt: 1, pb: 1 }} spacing={2}>
                            <Grid
                              item
                              xs={12}
                              md={6}
                              sx={{
                                textAlign: ["center", "center", "left"],
                              }}
                            >
                              <Typography sx={{ mb: 0, fontSize: "0.875rem" }}>
                                Refer a friend and earn Guardian rewards!
                              </Typography>
                              <Link
                                color="inherit"
                                href={`${
                                  getEnvironmentVariable(
                                    "HEADER_SOLFORGE_URL"
                                  ) || ""
                                }/forge-guardian`}
                                target="_blank"
                                underline="always"
                              >
                                <Typography variant="caption">
                                  Learn more
                                </Typography>
                              </Link>
                            </Grid>
                            <Grid
                              item
                              xs={12}
                              md={6}
                              sx={{
                                textAlign: ["center", "center", "right"],
                              }}
                            >
                              <Tooltip open={referralTooltip} title="Copied">
                                <Typography
                                  sx={{
                                    mb: 0,
                                    fontSize: "0.875rem",
                                    cursor: "pointer",
                                    textDecoration: "underline",
                                  }}
                                  onClick={() => {
                                    navigator.clipboard.writeText(
                                      `${
                                        getEnvironmentVariable(
                                          "HEADER_SOLFORGE_URL"
                                        ) !== ""
                                          ? getEnvironmentVariable(
                                              "HEADER_SOLFORGE_URL"
                                            )
                                          : window.location.origin
                                      }/?referral=${
                                        authContext?.authState?.identity
                                          ?.username
                                      }`
                                    );
                                    setReferralTooltip(true);
                                  }}
                                >
                                  {getEnvironmentVariable(
                                    "HEADER_SOLFORGE_URL"
                                  ) || window.location.origin}
                                  /?referral=
                                  {
                                    authContext?.authState?.identity?.username
                                  }{" "}
                                  <BCPCopy style={{ "font-style": 14 }} />
                                </Typography>
                              </Tooltip>
                            </Grid>
                          </Grid>
                        </Box>
                      </Grid>
                    )}

                  {getEnvironmentVariable("IS_SOLFORGE") === "true" && (
                    <Grid
                      item
                      xs={12}
                      sx={{
                        p: 2,
                        borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                        borderRadius: 1,
                        boxShadow: 1,
                        backgroundColor: "background.secondary",
                        textAlign: "left",
                      }}
                    >
                      <Typography variant="h4" gutterBottom>
                        Achievements
                      </Typography>

                      <ListResponsive
                        originalList={
                          achievements
                            ? Object.values(achievements)
                            : Object.values(initialAchievements)
                        }
                        rowProps={{
                          headings: (
                            <Hidden smDown>
                              <Grid
                                item
                                xs={12}
                                sx={{
                                  p: 1,
                                  borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                                  borderRadius: "4px 4px 0 0",
                                  backgroundColor: "table.headerBackground",
                                  color: "table.headerColor",
                                }}
                              >
                                <AchievementRow heading />
                              </Grid>
                            </Hidden>
                          ),
                          mapFunction: (row, index) => (
                            <Grid
                              item
                              xs={12}
                              key={index}
                              sx={{
                                p: 1,
                                borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
                                backgroundColor: "table.oddRowBackground",
                                color: "table.rowColor",

                                "&:nth-of-type(odd)": {
                                  backgroundColor: "table.evenRowBackground",
                                },
                              }}
                            >
                              <AchievementRow
                                row={row}
                                index={index}
                                embed={embed}
                                loading={!Boolean(achievements)}
                              />
                            </Grid>
                          ),
                        }}
                      />
                    </Grid>
                  )}

                  {hidePlacings ? (
                    <Grid
                      item
                      xs={12}
                      sx={{
                        p: 2,
                        textAlign: "center",
                      }}
                    >
                      <Typography variant="h4" gutterBottom>
                        The Circuit owner currently has Rankings hidden
                      </Typography>
                    </Grid>
                  ) : (
                    <>
                      {league &&
                        league?.defaultPlacingType === "ELO Points" && (
                          <Grid item xs={12}>
                            <Grid container spacing={2}>
                              <Grid item xs={12} md={6}>
                                <Grid
                                  container
                                  sx={{
                                    border: "1px solid rgba(0, 0, 0, 0.12)",
                                    borderRadius: 1,
                                    boxShadow: 1,

                                    backgroundColor: "background.tertiary",
                                  }}
                                >
                                  <Grid
                                    item
                                    xs={6}
                                    textAlign="left"
                                    sx={{
                                      p: 2,
                                    }}
                                  >
                                    <Typography variant="h5">Points</Typography>
                                  </Grid>
                                  <Grid
                                    item
                                    xs={6}
                                    textAlign="right"
                                    sx={{
                                      p: 2,
                                    }}
                                  >
                                    {placing && (
                                      <Typography variant="h4">
                                        {placing.ELOPoints}
                                      </Typography>
                                    )}
                                  </Grid>
                                </Grid>
                              </Grid>

                              <Grid item xs={12} md={6}>
                                <Grid
                                  container
                                  sx={{
                                    border: "1px solid rgba(0, 0, 0, 0.12)",
                                    borderRadius: 1,
                                    boxShadow: 1,
                                    backgroundColor: "background.tertiary",
                                  }}
                                >
                                  <Grid
                                    item
                                    xs={6}
                                    textAlign="left"
                                    sx={{
                                      p: 2,
                                    }}
                                  >
                                    <Typography variant="h5">Class</Typography>
                                  </Grid>
                                  <Grid
                                    item
                                    xs={6}
                                    textAlign="right"
                                    sx={{
                                      p: 2,
                                    }}
                                  >
                                    {placing && (
                                      <Typography variant="h4">
                                        {placing.ELOClass}
                                      </Typography>
                                    )}
                                  </Grid>
                                </Grid>
                              </Grid>

                              <Grid item xs={12} md={6}>
                                <Grid
                                  container
                                  sx={{
                                    border: "1px solid rgba(0, 0, 0, 0.12)",
                                    borderRadius: 1,
                                    boxShadow: 1,
                                    backgroundColor: "background.tertiary",
                                  }}
                                >
                                  <Grid
                                    item
                                    xs={6}
                                    textAlign="Left"
                                    sx={{
                                      p: 2,
                                    }}
                                  >
                                    <Typography variant="h5">
                                      Games Played
                                    </Typography>
                                  </Grid>
                                  <Grid
                                    item
                                    xs={6}
                                    textAlign="right"
                                    sx={{
                                      p: 2,
                                    }}
                                  >
                                    {placing && (
                                      <Typography variant="h4">
                                        {placing.resultRecord?.length}
                                      </Typography>
                                    )}
                                  </Grid>
                                </Grid>
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <Grid
                                  container
                                  sx={{
                                    border: "1px solid rgba(0, 0, 0, 0.12)",
                                    borderRadius: 1,
                                    boxShadow: 1,
                                    backgroundColor: "background.tertiary",
                                  }}
                                >
                                  <Grid
                                    item
                                    xs={6}
                                    textAlign="left"
                                    sx={{
                                      p: 2,
                                    }}
                                  >
                                    <Typography variant="h5">
                                      Win Ratio
                                    </Typography>
                                  </Grid>
                                  <Grid
                                    item
                                    xs={6}
                                    textAlign="right"
                                    sx={{
                                      p: 2,
                                    }}
                                  >
                                    {placing && (
                                      <Typography variant="h4">
                                        {placing.winRatio}%
                                      </Typography>
                                    )}
                                  </Grid>
                                </Grid>
                              </Grid>
                            </Grid>
                          </Grid>
                        )}

                      <Grid item xs={12}>
                        <ListResponsive
                          originalList={results}
                          doSearch={true}
                          searchProps={{
                            label: "Search Placings",
                            performSearch: (originalList, searchText) => {
                              if (!searchText || searchText === "") {
                                return originalList;
                              } else {
                                var filteredList = originalList.filter(
                                  function (el) {
                                    if (
                                      el?.event?.name
                                        ?.toLowerCase()
                                        .includes(searchText)
                                    )
                                      return true;
                                    if (
                                      el?.faction?.name
                                        ?.toLowerCase()
                                        .includes(searchText)
                                    )
                                      return true;
                                    if (
                                      el?.team?.name
                                        ?.toLowerCase()
                                        .includes(searchText)
                                    )
                                      return true;
                                    return false;
                                  }
                                );
                                return filteredList;
                              }
                            },
                          }}
                          rowProps={{
                            headings: (
                              <Grid item xs={12}>
                                <Typography
                                  variant="h6"
                                  color="text.secondary"
                                  gutterBottom
                                >
                                  Placing
                                </Typography>
                              </Grid>
                            ),
                            mapFunction: (row, index) => (
                              <Grid
                                item
                                xs={12}
                                key={index}
                                sx={{
                                  p: 1,
                                }}
                              >
                                <PlacingRow
                                  row={row}
                                  index={index}
                                  leagueId={leagueId}
                                  embed={embed}
                                  league={league}
                                  setRefresh={setRefresh}
                                  manageCorrections={manageCorrections}
                                  setCorrection={setCorrection}
                                  setOptionsAnchorEl={setOptionsAnchorEl}
                                />
                              </Grid>
                            ),
                          }}
                        />
                      </Grid>
                    </>
                  )}
                </Grid>
              </TabPanel>
              <TabPanel value={activeTab} index={tabIndexes.about}>
                <Typography variant="h3" gutterBottom>
                  About
                </Typography>
                <Divider sx={{ mb: 1 }} />
                {player.bio && player.bio !== "" && (
                  <Typography variant="body1" gutterBottom paragraph>
                    {player.bio.split("\n").map((i, key) => {
                      return <Box key={key}>{i}</Box>;
                    })}
                  </Typography>
                )}
              </TabPanel>
              <TabPanel value={activeTab} index={tabIndexes.Circuits}>
                <Typography variant="h3" gutterBottom>
                  Circuits
                </Typography>
                <Divider sx={{ mb: 1 }} />
              </TabPanel>
            </Box>
          </Container>

          {manageCorrections && (
            <>
              <OptionsMenu
                anchorEl={optionsAnchorEl}
                setAnchorEl={setOptionsAnchorEl}
                league={league}
                setCorrectionType={setCorrectionType}
                setCorrection={setCorrection}
              />
              {correctionType === "updatePlacingArmy" && (
                <UpdateArmyPlacingModal
                  league={league}
                  setCorrectionType={setCorrectionType}
                  correction={correction}
                  setCorrection={setCorrection}
                  setProcessing={setProcessing}
                  processing={processing}
                  setRefresh={setRefresh}
                  t={t}
                />
              )}
              {correctionType === "updatePlacingTeam" && (
                <UpdateTeamPlacingModal
                  league={league}
                  setCorrectionType={setCorrectionType}
                  correction={correction}
                  setCorrection={setCorrection}
                  setProcessing={setProcessing}
                  processing={processing}
                  setRefresh={setRefresh}
                />
              )}
            </>
          )}
        </Box>
      )}
    </>
  );
}

const AchievementRow = ({ heading, row, loading, embed }) => {
  const mediaLT600 = useMediaQuery("(max-width:599px)");
  if (mediaLT600) {
    if (heading) return <></>;
    return (
      <Grid container spacing={1} alignItems="center" sx={{ fontSize: "16px" }}>
        <Grid item xs={6}>
          Tier
        </Grid>
        <Grid item xs={6}>
          <CustomChip
            label={row.level}
            wrapperSX={{
              background: row.chip?.background,
              color: row.chip?.color,
              border: row.chip?.border ? `2px solid ${row.chip?.border}` : null,
              pt: "4px",
              pb: "4px",
            }}
          />
        </Grid>
        <Grid item xs={6}>
          Seasonal Points
        </Grid>
        <Grid item xs={6}>
          {loading ? (
            <CircularProgress size={16} />
          ) : (
            parseInt(row.current?.totalPoints || 0)
          )}
        </Grid>
        <Grid item xs={6}>
          All-Time Points
        </Grid>
        <Grid item xs={6}>
          {loading ? (
            <CircularProgress size={16} />
          ) : (
            parseInt(row.current?.lifeTime || 0)
          )}
        </Grid>
        <Grid item xs={12}>
          <Link
            href={`/rankings/players?league=${row.leagueId}`}
            target="_blank"
            underline="none"
          >
            See Rankings
          </Link>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={1} alignItems="center" sx={{ fontSize: "16px" }}>
      <Grid
        item
        sx={{
          minWidth: ["100%", "110px", "25%"],
        }}
      >
        {heading ? (
          "Tier"
        ) : (
          <CustomChip
            label={row.level}
            wrapperSX={{
              background: row.chip?.background,
              color: row.chip?.color,
              border: row.chip?.border ? `2px solid ${row.chip?.border}` : null,
            }}
          />
        )}
      </Grid>
      <Grid
        item
        sx={{
          minWidth: ["100%", "135px", "25%"],
        }}
      >
        {heading ? (
          "Seasonal Points"
        ) : loading ? (
          <CircularProgress size={16} />
        ) : (
          parseInt(row.current?.totalPoints || 0)
        )}
      </Grid>
      <Grid
        item
        sx={{
          minWidth: ["100%", "135px", "25%"],
        }}
      >
        {heading ? (
          "All-Time Points"
        ) : loading ? (
          <CircularProgress size={16} />
        ) : (
          parseInt(row.lifeTime?.totalPoints || 0)
        )}
      </Grid>
      <Grid
        item
        sx={{
          flex: [null, 1],
          minWidth: ["100%", "unset"],
        }}
      >
        {heading ? (
          ""
        ) : (
          <Link
            href={`/rankings/players?league=${row.leagueId}`}
            target="_blank"
            underline="none"
          >
            See Rankings
          </Link>
        )}
      </Grid>
    </Grid>
  );
};

const PlacingRow = ({
  manageCorrections,
  setCorrection,
  setOptionsAnchorEl,
  heading,
  row,
  index,
  leagueId,
  embed,
  league,
}) => {
  return (
    <Grid key={index} container spacing={1} sx={{ fontSize: "16px" }}>
      <Grid
        item
        // xs
        sx={{
          width: "100px",
          maxWidth: "100px",
        }}
      >
        <Typography>{`${
          row.placing < 10 ? `0${row.placing}` : row.placing
        }`}</Typography>
        {!getEnvironmentVariable("HIDE_RANKINGS") &&
          league?.defaultPlacingType !== "ELO Points" && (
            <Typography>{row.points || 0}</Typography>
          )}
      </Grid>

      <Grid item xs>
        <Typography>
          <Link
            href={`/truncated/event/${
              row.event?.id
            }?active_tab=placings&embed=${embed ? "true" : "false"}${
              leagueId ? `&league=${leagueId}` : ""
            }`}
            underline="none"
          >
            {row.event?.name}
          </Link>
        </Typography>
        {Boolean(row.faction || row.team) && (
          <Typography
            variant="caption"
            sx={{ display: ["inherit", "inherit", "none"] }}
          >
            {row.faction && (
              <Link
                href={`/rankings/players?embed=${
                  embed ? "true" : "false"
                }&army=${row.faction.id}${
                  leagueId ? `&league=${leagueId}` : ""
                }`}
                underline="none"
              >
                {row.faction.name}
                {row.character?.name || row.subFaction?.name
                  ? ` - ${row.character?.name || row.subFaction?.name}`
                  : ""}
              </Link>
            )}
            {Boolean(row.faction?.id && row.team?.id) && " - "}
            {row.team && (
              <Link
                href={`/team/${row.team.id}?embed=${embed ? "true" : "false"}${
                  leagueId ? `&league=${leagueId}` : ""
                }`}
                underline="none"
              >
                {row.team.name}
              </Link>
            )}
          </Typography>
        )}
      </Grid>
      {league?.gameSystem?.hasFactions && (
        <Grid item xs sx={{ display: ["none", "none", "inherit"] }}>
          {row.faction && (
            <Link
              href={`/rankings/players?embed=${embed ? "true" : "false"}&army=${
                row.faction.id
              }${leagueId ? `&league=${leagueId}` : ""}`}
              underline="none"
            >
              {row.faction.name}
              {row.character?.name || row.subFaction?.name
                ? ` - ${row.character?.name || row.subFaction?.name}`
                : ""}
            </Link>
          )}
        </Grid>
      )}
      <Grid item xs sx={{ display: ["none", "none", "inherit"] }}>
        {row.team && (
          <Link
            href={`/team/${row.team.id}?embed=${embed ? "true" : "false"}${
              leagueId ? `&league=${leagueId}` : ""
            }`}
            underline="none"
          >
            {row.team.name}
          </Link>
        )}
      </Grid>

      {manageCorrections && heading && (
        <IconButton color="link">
          <BCPWrench />
        </IconButton>
      )}
      {manageCorrections && !heading && (
        <IconButton
          color="link"
          onClick={(e) => {
            setCorrection(row);
            setOptionsAnchorEl(e.currentTarget);
          }}
        >
          <BCPWrench />
        </IconButton>
      )}
      <Divider sx={{ mb: 1 }} />
    </Grid>
  );
};

function OptionsMenu(props) {
  const { league, anchorEl, setAnchorEl, setCorrectionType } = props;
  const open = Boolean(anchorEl);
  return (
    <>
      <StyledMenu
        id="options-menu"
        MenuListProps={{
          "aria-labelledby": "options-button",
        }}
        disableScrollLock
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
      >
        {league?.gameSystem?.hasFactions && (
          <MenuItem
            onClick={async (e) => {
              setCorrectionType("updatePlacingArmy");
              setAnchorEl(null);
            }}
            disableRipple
          >
            Change {league?.gameSystem?.factionLabel || "Faction"}
          </MenuItem>
        )}
        <MenuItem
          onClick={async (e) => {
            setCorrectionType("updatePlacingTeam");
            setAnchorEl(null);
          }}
          disableRipple
        >
          Change Team
        </MenuItem>
        <MenuItem
          onClick={async (e) => {
            setCorrectionType("zeroPlacing");
            setAnchorEl(null);
          }}
          disableRipple
        >
          Zero Placing
        </MenuItem>
        <MenuItem
          onClick={async (e) => {
            setCorrectionType("deletePlacing");
            setAnchorEl(null);
          }}
          disableRipple
        >
          Delete Placing
        </MenuItem>
      </StyledMenu>
    </>
  );
}

function UpdateArmyPlacingModal(props) {
  const {
    league,
    correction,
    setCorrection,
    setCorrectionType,
    setProcessing,
    processing,
    setRefresh,
    t
  } = props;

  const [armies, setArmies] = useState(props.armies || null);
  const [armyDict, setArmyDict] = useState(props.armyDict || {});

  const setClose = () => {
    setCorrection(null);
    setCorrectionType(null);
  };
  useEffect(() => {
    const check = async () => {
      if (!processing) setProcessing(true);

      const { armyArray, subFactionDict } = await getArmyPickerArmies(
        league?.gameSystem?.id || league?.gameSystemId
      );

      if (armyArray?.length) {
        setArmies(armyArray);
        setArmyDict(subFactionDict);
      }

      setProcessing(false);
    };
    check();
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <Modal
        disableAutoFocus
        open={Boolean(correction)}
        onClose={setClose}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: ["95vw", "85vw", 600],
            maxHeight: "95vh",
            overflowY: "scroll",
            bgcolor: "background.paper",
            border: "1px solid rgba(0, 0, 0, 0.12)",
            borderRadius: 1,
            boxShadow: 24,
            p: 4,
          }}
        >
          <IconButton
            aria-label="cancel"
            color="error"
            sx={{ position: "absolute", top: "4px", right: "4px" }}
            onClick={setClose}
          >
            <BCPXmark />
          </IconButton>
          <Grid
            container
            spacing={2}
            sx={{
              pb: 1,
              mb: 2,
              borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
            }}
          >
            <Grid item xs>
              <Typography variant="h4" component="h2">
                Select {league?.gameSystem?.factionLabel || "Faction"}
              </Typography>
            </Grid>
          </Grid>

          <Formik
            initialValues={{
              armyId: correction?.armyId || "",
              subFactionId: correction?.subFactionId || "",
            }}
            enableReinitialize
            validationSchema={Yup.object().shape({
              armyId: Yup.string().required("Required"),
              subFactionId: league?.gameSystem?.subFactionRequired
                ? Yup.string().required("Required")
                : Yup.string(),
            })}
            onSubmit={async (values) => {
              var body = {
                placingId: correction.id,
                armyId: values.armyId,
              };
              if (league?.gameSystem?.subFactionRequired) {
                if (values.subFactionId && values.subFactionId !== "") {
                  body.subFactionId = values.subFactionId;
                }
                if (
                  correction?.armyId === body.armyId &&
                  correction?.subFactionId === body.subFactionId
                ) {
                  postNotification("info", t("notifications.info.noUpdates"));
                  return;
                }
              } else {
                if (correction?.armyId === body.armyId) {
                  postNotification("info", t("notifications.info.noUpdates"));
                  return;
                }
              }

              setProcessing(true);
              try {
                await postData(`/leagues/${league.id}/updatePlacingArmy`, {
                  body: body,
                });

                await delay(3000);
                setRefresh(true);
              } catch (err) {
                postNotification("error", err);
              }
              setProcessing(false);
              setClose();
            }}
          >
            {({
              errors,
              handleBlur,
              handleChange,
              handleSubmit,
              isSubmitting,
              touched,
              values,
              setFieldValue,
              submitForm,
            }) => (
              <form onSubmit={handleSubmit}>
                <Grid
                  container
                  spacing={2}
                  sx={{
                    pb: 1,
                    mb: 2,
                  }}
                >
                  <Grid item xs={12}>
                    <ArmyPicker
                      armies={armies}
                      name="armyId"
                      label={league?.gameSystem?.factionLabel || "Faction"}
                      value={values.armyId}
                      handleChange={(e) => {
                        handleChange(e);
                        setFieldValue("subFactionId", "");
                      }}
                      error={Boolean(touched.armyId && errors.armyId)}
                      helperText={touched.armyId && errors.armyId}
                    />
                  </Grid>

                  {league?.gameSystem?.subFactionRequired && (
                    <Grid item xs={12}>
                      <GenericPicker
                        name="subFactionId"
                        label={
                          league?.gameSystem?.subFactionLabel || "Sub Faction"
                        }
                        value={values.subFactionId}
                        handleChange={handleChange}
                        inclAny={false}
                        list={armyDict?.[values.armyId] || []}
                        error={Boolean(
                          touched.subFactionId && errors.subFactionId
                        )}
                        helperText={touched.subFactionId && errors.subFactionId}
                        disabled={!values.armyId || values.armyId === ""}
                      />
                    </Grid>
                  )}

                  <Grid item xs={12} sm={6}>
                    <Button
                      color="error"
                      variant="outlined"
                      fullWidth
                      onClick={setClose}
                    >
                      Cancel
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Button
                      type="submit"
                      color="primary"
                      variant="contained"
                      fullWidth
                    >
                      Update
                    </Button>
                  </Grid>
                </Grid>
              </form>
            )}
          </Formik>
        </Box>
      </Modal>
    </>
  );
}

function UpdateTeamPlacingModal(props) {
  const {
    league,
    correction,
    setCorrection,
    setCorrectionType,
    setProcessing,
    setRefresh,
  } = props;

  const setClose = () => {
    setCorrection(null);
    setCorrectionType(null);
  };

  return (
    <>
      <Modal
        disableAutoFocus
        open={Boolean(correction)}
        onClose={setClose}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: ["95vw", "85vw", 600],
            maxHeight: "95vh",
            overflowY: "scroll",
            bgcolor: "background.paper",
            border: "1px solid rgba(0, 0, 0, 0.12)",
            borderRadius: 1,
            boxShadow: 24,
            p: 4,
          }}
        >
          <IconButton
            aria-label="cancel"
            color="error"
            sx={{ position: "absolute", top: "4px", right: "4px" }}
            onClick={setClose}
          >
            <BCPXmark />
          </IconButton>
          <Grid
            container
            spacing={2}
            sx={{
              pb: 1,
              mb: 2,
              borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
            }}
          >
            <Grid item xs>
              <Typography variant="h4" component="h2">
                Enter New Team Name
              </Typography>
            </Grid>
          </Grid>

          <Formik
            initialValues={{
              teamName: correction?.team?.name || "",
            }}
            enableReinitialize
            validationSchema={Yup.object().shape({
              teamName: Yup.string().max(128),
            })}
            onSubmit={async (values) => {
              var body = {
                placingId: correction.id,
                teamName: values.teamName,
              };

              setProcessing(true);
              try {
                await postData(`/leagues/${league.id}/updatePlacingTeam`, {
                  body: body,
                });

                await delay(3000);
                setRefresh(true);
              } catch (err) {
                postNotification("error", err);
              }
              setProcessing(false);
              setClose();
            }}
          >
            {({
              errors,
              handleBlur,
              handleChange,
              handleSubmit,
              isSubmitting,
              touched,
              values,
              setFieldValue,
              submitForm,
            }) => (
              <form onSubmit={handleSubmit}>
                <Grid
                  container
                  spacing={2}
                  sx={{
                    pb: 1,
                    mb: 2,
                  }}
                >
                  <Grid item xs={12}>
                    <TextField
                      name="teamName"
                      label="Club/Team Name"
                      value={values.teamName}
                      variant="outlined"
                      fullWidth
                      margin="normal"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      error={Boolean(touched.teamName && errors.teamName)}
                      helperText={touched.teamName && errors.teamName}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Button
                      color="error"
                      variant="outlined"
                      fullWidth
                      onClick={setClose}
                    >
                      Cancel
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Button
                      type="submit"
                      color="primary"
                      variant="contained"
                      fullWidth
                    >
                      Update
                    </Button>
                  </Grid>
                </Grid>
              </form>
            )}
          </Formik>
        </Box>
      </Modal>
    </>
  );
}
